import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Banner, Button, Card, ConfirmDialog, EmptyRow, Field, Segmented, SkeletonRows, Switch,
} from '../kit';
import {
  apiErrorMessage, createCurrencyPair, getCurrencyPairs, getCurrencyRates, removeCurrencyPair, saveCurrencyRate,
  updateCurrencyPair,
} from '../../../utils/organizationApi';
import { SUPPORTED_CURRENCIES } from '../../../utils/currency';
import {
  currencyOptions, dateKey, formatDateKey, formatRate, isTruthyFlag, manualRateDate, pairStatus,
} from './orgModel';
import { CurrencySelect, OrgDialog } from './orgsUi';

const GRANULARITIES = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'DAILY', label: 'Daily' },
];

const todayKey = () => new Date().toISOString().slice(0, 10);
const defaultPeriod = (granularity) => {
  const t = todayKey();
  if (granularity === 'YEARLY') return t.slice(0, 4);
  if (granularity === 'MONTHLY') return t.slice(0, 7);
  return t;
};
const pairLabel = (p) => `${p.fromCurrency} → ${p.toCurrency}`;

export default function CurrencyCard({ org, portfolioCurrency, currencies }) {
  const ids = { from: useId(), to: useId(), start: useId(), pair: useId(), gran: useId(), period: useId(), rate: useId() };
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pairForm, setPairForm] = useState(null);
  const [savingPair, setSavingPair] = useState(false);
  const [busyPair, setBusyPair] = useState({});
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [rateForm, setRateForm] = useState({ pairId: '', granularity: 'MONTHLY', period: defaultPeriod('MONTHLY'), rate: '' });
  const [savingRate, setSavingRate] = useState(false);
  const [historyPair, setHistoryPair] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const rows = await getCurrencyPairs(org.id);
      setPairs(rows);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load currency pairs.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => pairs.filter((p) => p.isActive === undefined || isTruthyFlag(p.isActive)), [pairs]);
  useEffect(() => {
    if (!active.length) return;
    setRateForm((prev) => (active.some((p) => String(p.id) === String(prev.pairId)) ? prev : { ...prev, pairId: String(active[0].id) }));
  }, [active]);

  const options = useMemo(() => currencyOptions(
    currencies,
    [...SUPPORTED_CURRENCIES, portfolioCurrency, ...pairs.flatMap((p) => [p.fromCurrency, p.toCurrency])]
  ), [currencies, pairs, portfolioCurrency]);

  const startAdd = () => {
    const to = portfolioCurrency || 'USD';
    const from = options.find((o) => o.value !== to)?.value || 'CAD';
    setPairForm({ fromCurrency: from, toCurrency: to, autoBackfillStartDate: '', autoDailyEnabled: true, allowInverse: true });
    setAdding(true);
  };

  const addPair = async () => {
    if (!pairForm) return;
    if (pairForm.fromCurrency === pairForm.toCurrency) { toast.error('Pick two different currencies.'); return; }
    setSavingPair(true);
    try {
      const saved = await createCurrencyPair({
        orgId: org.id,
        fromCurrency: pairForm.fromCurrency,
        toCurrency: pairForm.toCurrency,
        isActive: true,
        autoDailyEnabled: pairForm.autoDailyEnabled,
        allowInverse: pairForm.allowInverse,
        autoBackfillStartDate: pairForm.autoBackfillStartDate || null,
      });
      const etl = saved?.currencyRateEtl;
      if (pairForm.autoDailyEnabled && etl && !etl.error && !etl.skipped) toast.success(`${pairLabel(pairForm)} added — rate backfill started in the background`);
      else if (etl?.error) toast.error(`${pairLabel(pairForm)} added, but the rate backfill didn't start: ${etl.error}`);
      else toast.success(`${pairLabel(pairForm)} added`);
      setAdding(false);
      setPairForm(null);
      await load({ silent: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not add the pair.'));
    } finally {
      setSavingPair(false);
    }
  };

  const togglePair = async (pair, field, value) => {
    setBusyPair((prev) => ({ ...prev, [pair.id]: true }));
    setPairs((prev) => prev.map((p) => (p.id === pair.id ? { ...p, [field]: value } : p)));
    try {
      await updateCurrencyPair({ id: pair.id, orgId: org.id, [field]: value });
      await load({ silent: true });
    } catch (err) {
      setPairs((prev) => prev.map((p) => (p.id === pair.id ? { ...p, [field]: pair[field] } : p)));
      toast.error(apiErrorMessage(err, 'Could not update the pair.'));
    } finally {
      setBusyPair((prev) => { const next = { ...prev }; delete next[pair.id]; return next; });
    }
  };

  const doRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await removeCurrencyPair({ id: removeTarget.id, orgId: org.id });
      toast.success(`${pairLabel(removeTarget)} removed`);
      setRemoveTarget(null);
      await load({ silent: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove the pair.'));
    } finally {
      setRemoving(false);
    }
  };

  const rateDate = manualRateDate(rateForm.granularity, rateForm.period);
  const rateValue = Number(rateForm.rate);
  const canSaveRate = Boolean(rateForm.pairId && rateDate && Number.isFinite(rateValue) && rateValue > 0);
  const saveRate = async () => {
    if (!canSaveRate) return;
    setSavingRate(true);
    try {
      await saveCurrencyRate({
        orgId: org.id,
        pairId: Number(rateForm.pairId),
        granularity: rateForm.granularity,
        rateDate,
        rate: rateValue,
        source: 'MANUAL_ADMIN',
      });
      toast.success('Rate saved');
      setRateForm((prev) => ({ ...prev, rate: '' }));
      await load({ silent: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the rate.'));
    } finally {
      setSavingRate(false);
    }
  };

  const selectedPair = active.find((p) => String(p.id) === String(rateForm.pairId)) || null;

  return (
    <Card
      title="Currency"
      actions={!adding ? <Button size="small" icon={Plus} onClick={startAdd} disabled={loading || Boolean(error)}>Add pair</Button> : null}
    >
      <span className="ak-help">Pairs convert location currencies into the portfolio currency ({portfolioCurrency || 'USD'}). Adding a pair starts a rate backfill in the background.</span>

      {error ? (
        <Banner tone="bad" icon={AlertTriangle} title="Couldn't load currency pairs" action={<Button size="small" onClick={() => load()}>Try again</Button>}>
          {error}
        </Banner>
      ) : null}

      {adding && pairForm ? (
        <div className="orgs-inset" role="group" aria-label="Add a currency pair">
          <span className="ak-label">New pair</span>
          <div className="ak-grid ak-3">
            <Field label="From" htmlFor={ids.from}>
              <CurrencySelect id={ids.from} value={pairForm.fromCurrency} options={options} onChange={(v) => setPairForm((p) => ({ ...p, fromCurrency: v }))} />
            </Field>
            <Field label="To" htmlFor={ids.to}>
              <CurrencySelect id={ids.to} value={pairForm.toCurrency} options={options} onChange={(v) => setPairForm((p) => ({ ...p, toCurrency: v }))} />
            </Field>
            <Field label="Backfill from" htmlFor={ids.start}>
              <input id={ids.start} type="date" className="ak-input" value={pairForm.autoBackfillStartDate} max={todayKey()} onChange={(e) => setPairForm((p) => ({ ...p, autoBackfillStartDate: e.target.value }))} />
            </Field>
          </div>
          <div className="orgs-inline-actions">
            <Switch checked={pairForm.autoDailyEnabled} onChange={(v) => setPairForm((p) => ({ ...p, autoDailyEnabled: v }))} label="Fetch rates daily" />
            <Switch checked={pairForm.allowInverse} onChange={(v) => setPairForm((p) => ({ ...p, allowInverse: v }))} label="Allow inverse" />
            <span className="ak-foot-spacer" />
            <Button size="small" onClick={() => { setAdding(false); setPairForm(null); }} disabled={savingPair}>Cancel</Button>
            <Button size="small" variant="primary" onClick={addPair} disabled={savingPair}>{savingPair ? 'Adding…' : 'Add pair'}</Button>
          </div>
        </div>
      ) : null}

      <div className="orgs-mini-wrap">
        <table className="orgs-mini-tbl">
          <thead>
            <tr>
              <th scope="col">Pair</th>
              <th scope="col">Auto daily</th>
              <th scope="col">Inverse</th>
              <th scope="col">Latest rate</th>
              <th scope="col">Status</th>
              <th scope="col"><span className="ak-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <SkeletonRows rows={2} columns={6} /> : null}
            {!loading && !error && active.length === 0 ? (
              <EmptyRow colSpan={6} title="No currency pairs" body="Every location reports in the portfolio currency, or add a pair to convert." />
            ) : null}
            {!loading && active.map((p) => {
              const st = pairStatus(p);
              const latest = dateKey(p.latestRateDate);
              return (
                <tr key={p.id}>
                  <td className="ak-mono">{pairLabel(p)}</td>
                  <td>
                    <Switch
                      checked={isTruthyFlag(p.autoDailyEnabled)}
                      disabled={Boolean(busyPair[p.id])}
                      onChange={(v) => togglePair(p, 'autoDailyEnabled', v)}
                      title={`Fetch ${pairLabel(p)} rates daily`}
                    />
                  </td>
                  <td>
                    <Switch
                      checked={isTruthyFlag(p.allowInverse)}
                      disabled={Boolean(busyPair[p.id])}
                      onChange={(v) => togglePair(p, 'allowInverse', v)}
                      title={`Also use ${pairLabel(p)} inverted (${p.toCurrency} → ${p.fromCurrency})`}
                    />
                  </td>
                  <td className="ak-mono">{latest ? `${formatRate(p.latestRate)} · ${formatDateKey(latest)}` : '—'}</td>
                  <td><span className={`orgs-tone-${st.tone}`}>{st.text}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <Button size="small" variant="ghost" onClick={() => setRemoveTarget(p)}>Remove</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {active.length ? (
        <div className="orgs-inset" role="group" aria-label="Enter a manual rate">
          <span className="ak-label">Enter a manual rate</span>
          <div className="ak-grid ak-4">
            <Field label="Pair" htmlFor={ids.pair}>
              <select id={ids.pair} className="ak-select" value={rateForm.pairId} onChange={(e) => setRateForm((p) => ({ ...p, pairId: e.target.value }))}>
                {active.map((p) => <option key={p.id} value={p.id}>{pairLabel(p)}</option>)}
              </select>
            </Field>
            <Field label="Rate type" htmlFor={ids.gran}>
              <select
                id={ids.gran}
                className="ak-select"
                value={rateForm.granularity}
                onChange={(e) => setRateForm((p) => ({ ...p, granularity: e.target.value, period: defaultPeriod(e.target.value) }))}
              >
                {GRANULARITIES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </Field>
            <Field label={rateForm.granularity === 'YEARLY' ? 'Year' : rateForm.granularity === 'MONTHLY' ? 'Month' : 'Date'} htmlFor={ids.period}>
              {rateForm.granularity === 'YEARLY' ? (
                <input id={ids.period} className="ak-input" type="number" min="2000" max="2100" value={rateForm.period} onChange={(e) => setRateForm((p) => ({ ...p, period: e.target.value }))} />
              ) : (
                <input
                  id={ids.period}
                  className="ak-input"
                  type={rateForm.granularity === 'MONTHLY' ? 'month' : 'date'}
                  value={rateForm.period}
                  placeholder={rateForm.granularity === 'MONTHLY' ? 'YYYY-MM' : 'YYYY-MM-DD'}
                  onChange={(e) => setRateForm((p) => ({ ...p, period: e.target.value }))}
                />
              )}
            </Field>
            <Field label="Rate" htmlFor={ids.rate} help={selectedPair ? `1 ${selectedPair.fromCurrency} in ${selectedPair.toCurrency}` : null}>
              <input
                id={ids.rate}
                className="ak-input ak-mono"
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                placeholder="Rate"
                value={rateForm.rate}
                onChange={(e) => setRateForm((p) => ({ ...p, rate: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') saveRate(); }}
              />
            </Field>
          </div>
          <div className="orgs-inline-actions">
            {selectedPair ? (
              <button type="button" className="ak-link" onClick={() => setHistoryPair(selectedPair)}>
                Rate history ({Number(selectedPair.rateCount) || 0})
              </button>
            ) : null}
            <span className="ak-foot-spacer" />
            <Button size="small" onClick={saveRate} disabled={!canSaveRate || savingRate}>{savingRate ? 'Saving…' : 'Save rate'}</Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title={removeTarget ? `Remove ${pairLabel(removeTarget)}?` : 'Remove pair?'}
        confirmLabel="Remove pair"
        danger
        busy={removing}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={doRemove}
      >
        <p style={{ margin: 0 }}>
          Reports stop converting with this pair and its daily rate fetch stops.
          Its {Number(removeTarget?.rateCount) || 0} saved rates are kept, and adding the same pair again brings them back.
        </p>
      </ConfirmDialog>

      <RateHistoryDialog org={org} pair={historyPair} onClose={() => setHistoryPair(null)} />
    </Card>
  );
}

const PAGE_SIZE = 25;

function RateHistoryDialog({ org, pair, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [granularity, setGranularity] = useState('ALL');
  const [sort, setSort] = useState({ field: 'rateDate', dir: 'desc' });
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!pair) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(0);
    setGranularity('ALL');
    getCurrencyRates({ orgId: org.id, pairId: pair.id })
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err) => { if (!cancelled) setError(apiErrorMessage(err, 'Could not load rates.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [org.id, pair]);

  const counts = useMemo(() => rows.reduce((acc, r) => ({ ...acc, [r.granularity]: (acc[r.granularity] || 0) + 1 }), {}), [rows]);
  const shown = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return rows
      .filter((r) => granularity === 'ALL' || r.granularity === granularity)
      .sort((a, b) => {
        if (sort.field === 'rate') return (Number(a.rate) - Number(b.rate)) * dir;
        return String(dateKey(a.rateDate)).localeCompare(String(dateKey(b.rateDate))) * dir;
      });
  }, [rows, granularity, sort]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const pageRows = shown.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const toggleSort = (field) => { setSort((s) => ({ field, dir: s.field === field && s.dir === 'desc' ? 'asc' : 'desc' })); setPage(0); };
  const arrow = (field) => (sort.field === field ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');
  const ariaSort = (field) => (sort.field === field ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');

  return (
    <OrgDialog open={Boolean(pair)} title={pair ? `Rate history · ${pairLabel(pair)}` : 'Rate history'} onClose={onClose}>
      <div className="orgs-rate-tools">
        <Segmented
          label="Rate type"
          value={granularity}
          onChange={(v) => { setGranularity(v); setPage(0); }}
          options={[
            { value: 'ALL', label: 'All', count: rows.length },
            { value: 'DAILY', label: 'Daily', count: counts.DAILY || 0 },
            { value: 'MONTHLY', label: 'Monthly', count: counts.MONTHLY || 0 },
            { value: 'YEARLY', label: 'Yearly', count: counts.YEARLY || 0 },
          ]}
        />
        {rows.length >= 500 ? <span className="ak-help">Showing the latest 500 rates.</span> : null}
      </div>
      {error ? <Banner tone="bad" icon={AlertTriangle} title="Couldn't load rates">{error}</Banner> : null}
      <div className="orgs-mini-wrap">
        <table className="orgs-mini-tbl">
          <thead>
            <tr>
              <th scope="col" aria-sort={ariaSort('rateDate')}><button type="button" className="ak-link" style={{ textDecoration: 'none', fontSize: 11 }} onClick={() => toggleSort('rateDate')}>Date{arrow('rateDate')}</button></th>
              <th scope="col">Type</th>
              <th scope="col" aria-sort={ariaSort('rate')}><button type="button" className="ak-link" style={{ textDecoration: 'none', fontSize: 11 }} onClick={() => toggleSort('rate')}>Rate{arrow('rate')}</button></th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <SkeletonRows rows={5} columns={4} /> : null}
            {!loading && !error && shown.length === 0 ? <EmptyRow colSpan={4} title="No rates" body="Nothing recorded for this rate type yet." /> : null}
            {!loading && pageRows.map((r) => (
              <tr key={r.id}>
                <td className="ak-mono">{formatDateKey(dateKey(r.rateDate), { year: true })}</td>
                <td>{String(r.granularity || '').toLowerCase()}</td>
                <td className="ak-mono">{formatRate(r.rate)}</td>
                <td className="ak-muted" style={{ fontSize: 12 }}>{r.source === 'MANUAL_ADMIN' ? 'Manual' : (r.provider || r.source || '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 ? (
        <div className="orgs-pager">
          <span>Page {page + 1} of {pages}</span>
          <Button size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</Button>
          <Button size="small" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next</Button>
        </div>
      ) : null}
    </OrgDialog>
  );
}
